/**
 * 通告偶遇文案池（docs/玩法设计.md §八 + 实现说明迁存章 A3；性格模板仅作文案选型依据，⛔ 上屏）
 * 每个性格模板一组；special 供随机 NPC 性格失配时的最后兜底（特殊 NPC ⛔ 走本表——
 * 它们的熟遇开口/闲聊读 NPCDef.encMetMeet/encMetChat，每人手写）。
 *
 * 2026-10-12 场景拆分改版：按「场景」拆成三套，各自按性格取——
 *  - set（片场）：拍摄周偶遇，行业相关占比高（聊戏/导演/通告）；
 *  - life（生活）：公园等自由出行偶遇，生活闲聊、行业降到最低；
 *  - visit（探班）：玩家主动去对方工作地探望，独立成池
 *    （⛔ 复用熟遇池——「特地探望」与「同环境偶遇」语义不同，用户定）。
 * 初遇反应 firstWarm 通用（⛔ 随场景分——「加个联系方式」这类反应不依赖地点）；
 * firstCold 已随「冷漠」选项删除（2026-10-12 用户定：无收益差的死选项）。
 * 熟遇四选一：闲聊读本表 metChat；送礼/邀约反应复用 NormalNpcData 的 giftTexts/inviteOkText/inviteFailText
 * （同文案池、⛔ 另立送礼池与邀约池）。
 * NPC 反应句由 VnDialog 原位连播（选项挂 reply），月度弹窗结果块只记账。
 */

/** 一套场景的偶遇台词池（片场/生活各一套；探班见 EncounterTemplate.visit） */
export interface EncounterScene {
    /** 初遇：NPC 开口（随机一条） */
    firstMeet: string[];
    /** 熟遇：NPC 开口 */
    metMeet: string[];
    /** 熟遇选「闲聊几句」后 NPC 反应（好感 ENCOUNTER_GAIN_CHAT） */
    metChat: string[];
}

export interface EncounterTemplate {
    /** 初遇选「认识一下」后 NPC 反应（通用，⛔ 随场景分） */
    firstWarm: string[];
    /** 片场场景（拍摄周偶遇，行业多） */
    set: EncounterScene;
    /** 生活场景（公园等自由出行偶遇，行业少） */
    life: EncounterScene;
    /** 探班（玩家主动探望，独立池）。2026-10-15：meet=开口句；闲聊 reply 走 metChat（⛔ 独立 visit.chat 池——已删） */
    visit: { meet: string[] };
}

export const ENCOUNTER_TEMPLATES: Record<'cheerful' | 'quiet' | 'sharp' | 'warm' | 'special', EncounterTemplate> = {
    cheerful: {
        firstWarm: [
            '哈哈哈哈我就知道能聊到一块儿！加个联系方式，回头一起出来玩啊，别放我鸽子！',
            '痛快！你这个人我一看就投缘，以后常约，就这么定啦！',
        ],
        set: {
            firstMeet: [
                '今天这场可够累的，你也刚下来？之前都没顾上跟你说上话，认识一下！',
                '哎，咱们一个组待这么久了还没正式认识，说不过去吧？我天天看你去化妆间，今天可得补上！',
            ],
            metMeet: [
                '哟又碰见啦！你也下来歇口气？我背词背得头大，正好拉你唠两句！',
                '哈！大老远我就认出你了，今天这场也有你吧？走走走，盒饭我帮你占一份！',
            ],
            metChat: [
                '跟你唠完这几句，我背得都顺了！下回你台词卡壳也来找我，我帮你顺，管用！',
                '导演今天又改本子了，我昨儿背的全白背，你说气不气人哈哈哈——不过也习惯了，他哪次不是临场改！',
            ],
        },
        life: {
            firstMeet: [
                '这么巧！我刚还说一个人逛没意思，你这就冒出来了，缘分啊缘分！',
                '哇你也在看这个？我还以为就我喜欢呢！来来来认识一下，我请你喝奶茶！',
            ],
            metMeet: [
                '天呐这么巧！我正念叨你呢，你就出现了，是不是听见我心里话啦？',
                '喏，奶茶我多买了一杯，正愁没人陪我喝第二口，便宜你了！',
            ],
            metChat: [
                '跟你唠这几句心里舒坦多了，比刷一小时热搜还解压，说真的！',
                '刚跟你数落完堵车，我那份外卖估计都凉透了——算了不提它，见着你比吃口热的还值！',
            ],
        },
        visit: {
            meet: [
                '你怎么跑来了！太好了！',
                '来了也不早说！',
            ],
        },
    },
    quiet: {
        firstWarm: [
            '嗯，谢谢。你的名字，我会记住的。',
            '那以后常聊。我话不多，你别介意就行。',
        ],
        set: {
            firstMeet: [
                '刚候场看你一个人坐那儿背词，就没好意思打扰。其实我们是不是在哪见过？',
                '打扰了。都是一个组的，之前没好意思跟你说话，今天想找个人聊聊。',
            ],
            metMeet: [
                '好久没见。你今天的戏我远远看了两眼，很好。',
                '坐会儿？我候场，心里有点乱，想找个人待着。',
            ],
            metChat: [
                '跟你说话我放松多了。这组里人来人往，就你能让我静下来。',
                '你该去准备了。我就在这儿待着，你再经过的时候跟我说一声就行。',
            ],
        },
        life: {
            firstMeet: [
                '你好。这个位子没人坐吧？没别的，就想坐这儿看会儿书。',
                '你是不是也常来这家店？我见过你几次了，一直没敢打招呼。',
            ],
            metMeet: [
                '真巧，居然在这碰见你。这阵子都没见你出来走动了。',
                '刚在路口瞅见你，还不敢认，走近了才确定是你。',
            ],
            metChat: [
                '今天这句"好巧"我攒了好久了，说出来心里舒服多了。',
                '那你先忙，我坐这儿再待会儿，回见。',
            ],
        },
        visit: {
            meet: [
                '没想到你会来看我。',
                '你来啦。',
            ],
        },
    },
    sharp: {
        firstWarm: [
            '直来直去，我不讨厌。行，这朋友我记下了，以后见面别装不认识。',
            '那算你有点眼光。我们这就算认识了。',
        ],
        set: {
            firstMeet: [
                '坐。都一个组的，早就该打声招呼了。怎么称呼？',
                '刚那场你收尾那句有点东西。我一般不夸人，今天破回例。得了，认识下，我叫你什么好？',
            ],
            metMeet: [
                '稀客啊，最近都不带想起我的？还得我在这楼里堵你。',
                '啧，气色比上回好点，没把自个儿熬垮，算你还有点数。别得意，随口一说。',
            ],
            metChat: [
                '可以啊你，话比以前敢说了，人也比以前站得直了。早该这样。',
                '行，你这套我认。下回有话直说，别等我挤牙膏似的一句句问，累不累。',
            ],
        },
        life: {
            firstMeet: [
                '你常来？那往后见面的日子长着，认识一下，省得每次都点头装不认识。',
                '一起坐呗，挤什么。我就爱跟有眼缘的人搭个话。',
            ],
            metMeet: [
                '一个人逛？正好我闲，陪你走两步，省得你魂儿丢了找不见路。',
                '哟，财主今天有空出来啊。坐，这杯我还没付呢，正好给你个表现机会。',
            ],
            metChat: [
                '行了聊到这儿，我也该走。别让人以为咱俩多熟——虽然吧，是挺熟。',
                '光点头糊弄谁呢。不过算了，今儿心情好，放你一马。',
            ],
        },
        visit: {
            meet: [
                '嚯，稀客，你还记得我呢？',
                '算你有心，想到来探我班。',
            ],
        },
    },
    warm: {
        firstWarm: [
            '真好，我就知道我们有缘～以后就是朋友啦，常约呀。',
            '那说好啦，下次见面我请你吃饭！',
        ],
        set: {
            firstMeet: [
                '你一个人候场呀？我远远看你像有点累了，别太拼哦，要不要一起坐会儿？',
                '刚在化妆间见你补妆，气色有点疲惫呢，晚上回去要好好休息啦。',
            ],
            metMeet: [
                '好久没见啦，看着怎么像瘦了？这阵子是不是又没日没夜赶通告，记得按时吃饭呀。',
                '我刚路过茶水间多倒了一杯热水，就想着你会不会也在这边，给你暖暖手。',
            ],
            metChat: [
                '跟你聊这么几句，我忽然就不那么累了呢～你也一样哦，戏里的情绪散了戏要放下来，别都装心里呀。',
                '嗯嗯，我懂你的意思～做我们这行就是得学会自己疼自己，别什么都往心里搁呀。',
            ],
        },
        life: {
            firstMeet: [
                '你一个人吗？我看你坐这儿好一会儿了，是不是在等人呀？要不要先喝口热的暖暖？',
                '你好呀～我多嘴一句：你穿得有点单薄，起风了，得加件衣服哦。',
            ],
            metMeet: [
                '好久没见着你啦，最近有好好吃饭吗？我总觉得你又瘦了点呢。',
                '我刚买了好些热乎的小吃，正愁一个人吃不完，正好你来了，陪我一起嘛～',
            ],
            metChat: [
                '和你说话真舒服，像喝了杯热茶一样～下次心里不痛快，也来找我，我一直在的。',
                '嗯呢，那你快去忙吧，别太辛苦了，记得多喝水、按时吃饭哦。',
            ],
        },
        visit: {
            meet: [
                '太好了！我正念叨你呢。',
                '好久不见啊。',
            ],
        },
    },
    special: {
        firstWarm: [
            '好，那今天就算认识了。以后有机会，常来往。',
        ],
        set: {
            firstMeet: [
                '都在这楼里做事，早就该打声招呼了。今天碰上了，认识一下。',
            ],
            metMeet: [
                '巧了，又见面。最近气色不错，看来过得挺顺心，过来说两句？',
            ],
            metChat: [
                '这行起起落落，难得有你这样不浮躁的。以后有合适的活儿，我头一个记着你。',
            ],
        },
        life: {
            firstMeet: [
                '一个人出来透气？我也是。坐吧，相识一场就是缘分。',
            ],
            metMeet: [
                '有阵子没见你出来走动了，过来坐，陪我喝杯东西。',
            ],
            metChat: [
                '跟你说几句，心里敞亮不少。去吧，别耽误正事，回见。',
            ],
        },
        visit: {
            meet: [
                '是你啊。怎么想起来看我了，坐。',
            ],
        },
    },
};

/** 初遇固定选项文案（2026-10-12 用户定：原「友好/冷漠」二选，随「冷漠」选项删除收敛为单选项「认识一下」） */
export const FIRST_MEET_CHOICES = {
    meet: '认识一下',
} as const;

/**
 * 熟遇四选一固定文案（10-10 改版；gain 见 GameConfig.ENCOUNTER_GAIN_CHAT 与 Items.ts 的 favor）。
 * 2026-10-12 场景拆分：invite/leave 去片场化（原「杀青前那顿我请」「回自己的通告」绑死片场、
 * 生活场景不成立），改通用措辞，片场/生活共用一套选项。
 * 2026-10-15 茄子令：选项文案改为简单词语（闲聊/送礼/邀约/离开），物品名由 reply/changes 承载。
 * 离开那一格⛔ NPC 反应句（用户定：不回应、不加好感、直接继续行程）。
 */
export const MET_CHOICES = {
    chat: '闲聊',
    gift: '送礼',
    invite: '邀约',
    leave: '离开',
} as const;


/**
 * 咖位差「够不着」分支（2026-10-12 用户令）：抽中咖位高于玩家 ≥
 * `GameConfig.ENCOUNTER_UNREACHABLE_RANK_GAP` 级的普通 NPC 时，NPC 不搭理你、暂时无法结识；
 * 播一句旁白（含官方原因提示）+ changes 条「人脉 -未结识」。不盖月戳；2026-10-16 撤保底后，这一档就是把本周那一次抽中机会花掉了，下周重新按概率掷。
 * 旁白不按性格差分（NPC 根本没接话，只作第三人称叙述）。`{name}` 由 EncounterSystem 替换为 NPC 名。
 */
export const UNREACHABLE_LINES = {
    /** 旁白（第三人称；用旁白态，不显示头像/姓名）——原因⛔ 写进旁白（2026-10-13 茄子令删），由变动行表达 */
    meet: '{name}目不斜视地从你面前经过。',
    /** 变动行（2026-10-13 茄子令：咖位不够的变动行显示这句，取代旧「人脉 -未结识」） */
    hint: '咖位还不足以结识此人',
} as const;

/**
 * NPC 主动打招呼文案池（方案 §二）：月初掷概率命中后，NPC 在聊天页发来的第一条消息。
 * 按性格模板取；special 供特殊 NPC（导演/制作人等）兜底。
 */
export const GREETING_LINES: Record<'cheerful' | 'quiet' | 'sharp' | 'warm' | 'special', string[]> = {
    cheerful: [
        '嘿！刷到你最近的采访啦，说得比演得还有意思，什么时候再聚聚？',
        '我这边刚杀青一台戏，闲下来怪想大家的，你最近忙不忙？',
        '看到好剧本第一时间就想到你了！什么时候有空聊两句？',
    ],
    quiet: [
        '……没什么要紧事，就是好久没联系了，问候一下。',
        '路过之前一起拍戏的地方，想起你了。最近还好吗？',
        '（发来一张收工时的晚霞照片）……觉得你会喜欢。',
    ],
    sharp: [
        '刚看到你新剧的口碑，不错啊，没给我丢人。',
        '别太拼了，状态差的时候演出来的东西骗不了人——懂的都懂。',
        '有个本子挺适合你的，别人我不告诉，你考虑考虑？',
    ],
    warm: [
        '最近降温了，你在外面跑通告要多穿点呀。',
        '刷到你的路透照啦，气色真好，替你开心～',
        '没什么事，就是想问问你最近吃得香不香、睡得怎么样？',
    ],
    special: [
        '最近在看一个新项目，想起你来，有空聊聊。',
        '上次合作很愉快。之后有合适的机会，第一时间想到你。',
        '好久不见，最近进展如何？方便的话坐坐？',
    ],
};

/** 回应打招呼：主角的话（随机一条，接在 NPC 招呼语之后） */
export const GREETING_REPLY_ME = [
    '刚看到消息～你最近怎么样？',
    '哎呀好久不见！你也想起我啦？',
    '收到收到，改天一定约！',
    '谢谢你还惦记着我，心里暖暖的。',
];

/** 回应打招呼：NPC 收尾语（随机一条，好感 +1 挂在这条上） */
export const GREETING_REPLY_NPC = [
    '这就对了嘛，忙归忙，饭要好好吃！',
    '那说定了，下次片场见！',
    '（对方笑了笑）行，那不打扰你工作啦。',
    '有你这句话，这招呼就没白发～',
];

/** 客串邀请·玩家抉择后的定格气泡两口（方案=docs/好友客串设计.md §二.④）。
 *  玩家回话（下）＝动作本身，统一口径、⛔ 按性格差分；好友反应（上）按 4 性格 + special 分池。 */
export const GUEST_INVITE_REPLY_ME: Record<'accept' | 'decline', string[]> = {
    accept: ['行，算我一个！回头把本子发我看看。', '这戏我接了，档期让经纪人圈出来。'],
    decline: ['这阵子档期实在排不开，真不好意思。', '下回吧，这次怕是赶不上了。'],
};

/** 好友对玩家接受/婉拒的反应（按性格模板分池，语气贴 GUEST_INVITE_OPEN/GREETING_LINES） */
export const GUEST_INVITE_REPLY_NPC: Record<'cheerful' | 'quiet' | 'sharp' | 'warm' | 'special', Record<'accept' | 'decline', string[]>> = {
    cheerful: {
        accept: ['太好了，那我跟制片报一声！回头请你吃顿好的～', '就知道你不会见死不救！片场见，我给你留 C 位！'],
        decline: ['理解理解，忙是好事！那下次一定约啊，说定了！', '没事没事，你的戏要紧，咱不差这一回～'],
    },
    quiet: {
        accept: ['……谢谢。那我在片场等你。', '（轻轻回了个「好」字）……到时候见。'],
        decline: ['……嗯，你忙你的，我就是问问。', '（对方回了个「好」）……那，打扰了。'],
    },
    sharp: {
        accept: ['哼，算你够意思。镜头我给你留着，别演砸了。', '行，就知道你拿得起来。这次欠我的，下回记得还。'],
        decline: ['切，大忙人。行吧——下回你求我，我都不请了。', '知道了，架子越来越大了。（顿了顿）开玩笑的，你先忙。'],
    },
    warm: {
        accept: ['真的吗？太谢谢你啦，那阵子有你在，我心里就踏实了。', '辛苦你跑这一趟，别太累着，我会照顾好你的盒饭！'],
        decline: ['没关系的呀，你别放在心上，身体和戏都要紧～', '没事没事，你能回我消息我就开心啦，下次有空再聚。'],
    },
    special: {
        accept: ['太好了，那我跟剧组说一声，等你档期。', '谢谢赏脸，回头细节我们对接。'],
        decline: ['理解，档期冲突没办法，那下次有机会再合作。', '好的，不勉强，改天再约。'],
    },
};

/** 方向二·邀好友客串：玩家开口句（{script}=被邀的剧）——玩家主动动作，统一口径、⛔ 按好友性格差分 */
export const GUEST_ASK_ME = [
    '《{script}》这边有个特别合适的位子，来帮我撑个场子呗？无片酬啊，就冲你这份面子。',
    '跟你说声，《{script}》差一个有观众缘的脸，我想来想去只能是你。',
];

/** 方向二·邀好友客串：好友应允句（{script}=被邀的剧），按 4 性格 + special 分池（与方向一 GUEST_INVITE_REPLY_NPC 对称） */
export const GUEST_ASK_NPC: Record<'cheerful' | 'quiet' | 'sharp' | 'warm' | 'special', string[]> = {
    cheerful: [
        '行，你的场子我捧定了，回头通告单发我！',
        '《{script}》是吧？冲你这声招呼，档期我挤也得挤出来！',
    ],
    quiet: [
        '……好，那我看看档期。《{script}》，记下了。',
        '（对方顿了顿）……嗯，我来。片场见，不用多说什么。',
    ],
    sharp: [
        '帮你这一回，你可得记着。《{script}》——番位别给我排太靠后啊。',
        '行吧，谁让你亲自开口了。换别人，我理都不理。',
    ],
    warm: [
        '你去《{script}》的话，我当然来呀，正好也能看看你～',
        '好呀，别跟我客气，能帮上我就开心。片场记得按时吃饭。',
    ],
    special: [
        '没问题，《{script}》的档期我让经纪人协调，回头对接细节。',
        '好，角色定了吗？我这边尽量配合你的安排。',
    ],
};

/** 好友剧宣批（2026-10-28）：玩家开口句（{script}=要宣传的剧）——统一口径、⛔ 按好友性格差分（GUEST_ASK_ME 同款形制） */
export const FRIEND_PROMO_ME = [
    '《{script}》马上开播了，帮我发条微博吆喝一声呗？就动动手指的事。',
    '跟你商量个事，《{script}》开播了，宣传上借你个人气撑一撑？',
];

/** 好友剧宣批：好友应允句（{script}=要宣传的剧），按 4 性格 + special 分池（GUEST_ASK_NPC 同款形制） */
export const FRIEND_PROMO_NPC: Record<'cheerful' | 'quiet' | 'sharp' | 'warm' | 'special', string[]> = {
    cheerful: [
        '小事一桩！《{script}》的宣传我这就发，再@一圈朋友帮你一起转！',
        '《{script}》我等了好久，宣传包在我身上，你就看明天的热搜吧！',
    ],
    quiet: [
        '……嗯，发了。记得看《{script}》。',
        '（对方没多说什么，很快转发了）《{script}》，期待。',
    ],
    sharp: [
        '行，《{script}》我帮你转。但宣传语我可得自己写，你那文案拿不出手。',
        '帮你转一条《{script}》。就这一回啊，别惯得你事事都来找我。',
    ],
    warm: [
        '好呀好呀，我这就发，祝《{script}》大火～',
        '当然可以！《{script}》我小号也一起转，保证给你整得热热闹闹的。',
    ],
    special: [
        '没问题，《{script}》的物料发我，我让工作室排版走一波。',
        '可以，这条我亲自写。《{script}》我也想看了很久了。',
    ],
};

/**
 * 方向一·好友邀玩家客串：NPC 开口句（月初 roll 命中产出，{script}=好友在拍的剧）。
 * 按性格模板分池（与 GREETING_LINES 同款形制），语气贴各性格；接受/婉拒后的回话见 GUEST_INVITE_REPLY_*。
 */
export const GUEST_INVITE_OPEN: Record<'cheerful' | 'quiet' | 'sharp' | 'warm' | 'special', string[]> = {
    cheerful: [
        '我新戏《{script}》正缺一个亮眼的客串，第一个就想到你！来给我撑两天场子呗？',
        '《{script}》有场重戏，我自己演着都缺个对手——你来露一面呗，包管给你留足镜头！',
    ],
    quiet: [
        '……我在拍《{script}》，有场戏，想了很久，还是觉得你来最合适。方便的话，来客串一下？',
        '《{script}》这边有个客串的位子，不催你，你考虑一下就好。',
    ],
    sharp: [
        '《{script}》里有个角色，别人演我都看不上，也就你敢接。来不来？',
        '直说了，《{script}》我需要你这张脸镇场子。档期你自己抠，我等你话。',
    ],
    warm: [
        '最近一切都好吗？我这边《{script}》有个轻松的客串，想着换换脑子，你要不要来玩两天？',
        '《{script}》拍到一半，总差那么点意思——你来客串一下，我心里就踏实啦。',
    ],
    special: [
        '我手上《{script}》有个客串机会，觉得挺适合你，有兴趣来搭一场吗？',
        '《{script}》正在拍，有个角色想请你来客串，档期合适的话聊聊？',
    ],
};
